import { useEffect, useRef, useState } from "react";
import { vibrate } from "../hooks.ts";
import { Karta, Komunikat, Odznaka, Przycisk } from "../ui.tsx";
import { Kartonik, KostkaZwierzat, type Sciana } from "./KostkaZwierzat.tsx";

/**
 * Superfarmer — ekran tury.
 *
 * Tura ma dwa kroki i ekran jest zbudowany dokładnie wokół nich: najpierw
 * opcjonalna wymiana, potem rzut, który turę kończy. Kostki stoją przyklejone
 * u góry, bo pod nimi przewijają się stada wszystkich graczy, a wynik rzutu
 * jest tym, co się właśnie sprawdza.
 */

interface Strona {
  zwierze: string;
  ile: number;
}

export interface SuperfarmerView {
  phase: "lobby" | "tura" | "propozycja" | "finished";
  roundNo: number;
  hostId: string;
  players: string[];
  currentPlayerId: string | null;
  mojaTura: boolean;
  canStart: boolean;

  stada: Record<string, Record<string, number>>;
  bank: Record<string, number> | null;
  komplet: string[];
  nazwy: Record<string, string>;
  nazwyMnogie: Record<string, string>;
  kursy: Array<{ id: string; oddaje: Strona; bierze: Strona }>;

  ostatniSkutek: {
    gracz: string;
    rzut: [Sciana, Sciana];
    przyrost: Record<string, number>;
    strata: Record<string, number>;
    ocalily: string[];
  } | null;
  rzutow: number;
  wymianaZuzyta: boolean;
  wymianaMiedzyGraczami: boolean;
  dozwoloneKursy: Array<{ kurs: string; odwrotnie: boolean; maks: number }>;
  /** Kursy, na które stać mnie samego — do wymiany z graczem, gdzie pudełko nie gra roli. */
  mojeKursy: Array<{ kurs: string; odwrotnie: boolean; maks: number }>;

  propozycjaTrwa: boolean;
  propozycja: {
    od: string;
    do: string;
    kurs: string;
    razy: number;
    odwrotnie: boolean;
    wygasaAt: number;
  } | null;

  superfarmerzy: string[];
}

/** Kolejność wyświetlania stada — od najtańszego, tak jak na tabeli wymian. */
const PORZADEK = ["krolik", "owca", "swinia", "krowa", "kon", "malyPies", "duzyPies"];

const SCIANY: Sciana[] = ["krolik", "owca", "swinia", "krowa", "kon", "wilk", "lis"];

/**
 * Animacja rzutu.
 *
 * Wyzwalaczem jest **licznik rzutów partii**, nie zmiana ścian. Dwa króliki
 * pod rząd to najczęstszy wynik w tej grze (kostki mają po sześć królików na
 * dwanaście ścian), a porównywanie wartości uznałoby taki rzut za „nic się nie
 * stało" i kostka zastygłaby w miejscu.
 */
function useAnimacjaRzutu(rzutow: number, sciany: [Sciana, Sciana] | null) {
  const [pokazywane, setPokazywane] = useState<[Sciana, Sciana] | null>(sciany);
  const [rzucane, setRzucane] = useState(false);
  const poprzednie = useRef(rzutow);

  useEffect(() => {
    const bylRzut = rzutow > poprzednie.current;
    poprzednie.current = rzutow;

    if (!bylRzut || !sciany) {
      setPokazywane(sciany);
      setRzucane(false);
      return;
    }

    setRzucane(true);
    vibrate(30);

    let krok = 0;
    const timer = setInterval(() => {
      krok += 1;
      if (krok >= 7) {
        clearInterval(timer);
        setPokazywane(sciany);
        setRzucane(false);
        return;
      }
      const losowa = (): Sciana => SCIANY[Math.floor(Math.random() * SCIANY.length)] as Sciana;
      setPokazywane([losowa(), losowa()]);
    }, 70);

    return () => clearInterval(timer);
    // Zależność po liczniku: serwer przysyła widok także przy zmianach, które
    // kostek nie dotyczą — a te nie mają nic animować.
  }, [rzutow]);

  return { pokazywane, rzucane };
}

export function Superfarmer({
  view,
  nicki,
  mojeId,
  onStart,
  onRzut,
  onWymiana,
  onPropozycja,
  onOdpowiedz,
}: {
  view: SuperfarmerView;
  nicki: Record<string, string>;
  mojeId: string;
  onStart: () => void;
  onRzut: () => void;
  onWymiana: (kurs: string, razy: number, odwrotnie: boolean) => void;
  onPropozycja: (komu: string, kurs: string, razy: number, odwrotnie: boolean) => void;
  onOdpowiedz: (akceptuj: boolean) => void;
}) {
  const [wybrany, setWybrany] = useState<{ kurs: string; odwrotnie: boolean } | null>(null);
  const [razy, setRazy] = useState(1);
  const { pokazywane, rzucane } = useAnimacjaRzutu(view.rzutow, view.ostatniSkutek?.rzut ?? null);

  if (view.phase === "lobby") {
    return (
      <>
        <Karta>
          <div className="text-sm text-tekst-slaby mb-2">Gracze</div>
          <div className="space-y-1">
            {view.players.map((id) => (
              <div key={id} className="flex justify-between">
                <span>{nicki[id] ?? id}</span>
                {id === view.hostId ? <Odznaka>host</Odznaka> : null}
              </div>
            ))}
          </div>
        </Karta>
        <Komunikat>
          Każdy zaczyna z jednym królikiem. Za każdą parę zwierząt jednego gatunku — licząc
          posiadane i wyrzucone — dostajesz jedną sztukę ze stada. Konia i krowę zdobywa się
          wyłącznie wymianą. Wygrywa pierwszy komplet: koń, krowa, świnia, owca i królik.
        </Komunikat>
        {view.canStart ? (
          <Przycisk wariant="glowny" pelna onClick={onStart}>
            Zaczynamy
          </Przycisk>
        ) : (
          <Komunikat>Czekamy, aż host zacznie partię.</Komunikat>
        )}
      </>
    );
  }

  if (view.phase === "finished") {
    return <Wyniki view={view} nicki={nicki} />;
  }

  const czekaNaMnie = view.propozycja?.do === mojeId;
  const mojeStado = view.stada[mojeId] ?? {};
  const kursDo = (id: string) => view.kursy.find((kurs) => kurs.id === id) ?? null;

  return (
    <>
      {/*
        Kostki przyklejone u góry: pod spodem przewijają się stada czterech
        graczy i tabela wymian, a wynik ostatniego rzutu jest tym, co się
        sprawdza najczęściej. Tło kryjące, ujemne marginesy na całą szerokość.
      */}
      <div className="sticky top-0 z-10 -mx-4 border-b border-obrys bg-tlo px-4 pb-2 space-y-2">
        <div className="flex justify-center gap-4 pt-2">
          <KostkaZwierzat sciana={pokazywane?.[0] ?? null} kolor="zielona" rzucana={rzucane} />
          <KostkaZwierzat sciana={pokazywane?.[1] ?? null} kolor="czerwona" rzucana={rzucane} />
        </div>

        {!rzucane && view.ostatniSkutek ? (
          <OpisSkutku skutek={view.ostatniSkutek} view={view} nicki={nicki} />
        ) : null}

        {view.mojaTura && view.phase === "tura" ? (
          <Przycisk wariant="glowny" pelna onClick={onRzut} className="min-h-16 text-xl">
            Rzuć kostkami
          </Przycisk>
        ) : (
          <div className="text-center text-tekst-slaby py-1">
            {view.phase === "propozycja"
              ? czekaNaMnie
                ? "Masz propozycję wymiany"
                : `Trwa wymiana — ${nicki[view.currentPlayerId ?? ""] ?? "?"} czeka na odpowiedź`
              : `Tura: ${nicki[view.currentPlayerId ?? ""] ?? "?"}`}
          </div>
        )}
      </div>

      {view.propozycja ? (
        <PropozycjaKarta
          view={view}
          nicki={nicki}
          mojeId={mojeId}
          onOdpowiedz={onOdpowiedz}
        />
      ) : null}

      {view.mojaTura && view.phase === "tura" ? (
        <Wymiana
          view={view}
          nicki={nicki}
          mojeId={mojeId}
          wybrany={wybrany}
          razy={razy}
          onWybierz={(wybor) => {
            setWybrany(wybor);
            setRazy(1);
          }}
          onRazy={setRazy}
          onWymiana={onWymiana}
          onPropozycja={onPropozycja}
          kursDo={kursDo}
        />
      ) : null}

      <Karta>
        <div className="text-sm text-tekst-slaby mb-2">Twoje stado</div>
        <Stado stado={mojeStado} komplet={view.komplet} nazwy={view.nazwy} />
      </Karta>

      {view.players
        .filter((id) => id !== mojeId)
        .map((id) => (
          <Karta key={id}>
            <div className="text-sm text-tekst-slaby mb-2 flex justify-between">
              <span>{nicki[id] ?? id}</span>
              {view.superfarmerzy.includes(id) ? <Odznaka aktywna>Superfarmer</Odznaka> : null}
            </div>
            <Stado stado={view.stada[id] ?? {}} komplet={view.komplet} nazwy={view.nazwy} maly />
          </Karta>
        ))}

      {view.bank ? (
        <Karta>
          <div className="text-sm text-tekst-slaby mb-2">Stado główne</div>
          <Stado stado={view.bank} komplet={[]} nazwy={view.nazwy} maly />
        </Karta>
      ) : (
        <Komunikat>Stado główne jest w tej partii nieograniczone.</Komunikat>
      )}
    </>
  );
}

/** Rząd zwierząt z licznikami. Brakujące do kompletu są wyróżnione. */
function Stado({
  stado,
  komplet,
  nazwy,
  maly = false,
}: {
  stado: Record<string, number>;
  komplet: string[];
  nazwy: Record<string, string>;
  maly?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {PORZADEK.map((zwierze) => {
        const ile = stado[zwierze] ?? 0;
        const brakuje = komplet.includes(zwierze) && ile === 0;
        return (
          <div
            key={zwierze}
            title={nazwy[zwierze] ?? zwierze}
            className={[
              "flex items-center gap-1 rounded-xl px-2 py-1 border",
              brakuje ? "border-uwaga" : "border-obrys",
              ile === 0 ? "opacity-40" : "",
            ].join(" ")}
          >
            <Kartonik zwierze={zwierze} rozmiar={maly ? "maly" : "zwykly"} />
            <span className={maly ? "tabular text-sm" : "tabular text-lg font-semibold"}>{ile}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Co dał ostatni rzut — jednym zdaniem, bo w aucie nikt nie liczy kartoników. */
function OpisSkutku({
  skutek,
  view,
  nicki,
}: {
  skutek: NonNullable<SuperfarmerView["ostatniSkutek"]>;
  view: SuperfarmerView;
  nicki: Record<string, string>;
}) {
  const opisz = (wpisy: Record<string, number>) =>
    Object.entries(wpisy)
      .map(([zwierze, ile]) => `${ile} ${view.nazwyMnogie[zwierze] ?? zwierze}`)
      .join(", ");

  const kto = nicki[skutek.gracz] ?? skutek.gracz;
  const przyrost = Object.keys(skutek.przyrost).length > 0 ? opisz(skutek.przyrost) : null;
  const strata = Object.keys(skutek.strata).length > 0 ? opisz(skutek.strata) : null;
  const pies = skutek.ocalily.map((p) => view.nazwy[p] ?? p).join(", ");

  return (
    <div className="text-center text-sm space-y-0.5">
      {przyrost ? (
        <div className="text-akcent">
          {kto}: +{przyrost}
        </div>
      ) : null}
      {strata ? (
        <div className="text-blad">
          {kto} traci {strata}
        </div>
      ) : null}
      {pies ? <div className="text-uwaga">Obronił {pies} — wraca do pudełka</div> : null}
      {!przyrost && !strata && !pies ? <div className="text-tekst-slaby">Bez zmian</div> : null}
    </div>
  );
}

/**
 * Panel wymiany: najpierw kurs, potem z kim.
 *
 * Dwa kroki zamiast jednej siatki wszystkich kombinacji, bo kursów jest sześć
 * razy dwa kierunki, a partnerów do czterech — jeden ekran z tego nie wyjdzie
 * na telefonie. Kursy niedostępne ze stadem są przygaszone, ale klikalne:
 * z graczem można je wykonać nawet wtedy, gdy w pudełku pusto.
 */
function Wymiana({
  view,
  nicki,
  mojeId,
  wybrany,
  razy,
  onWybierz,
  onRazy,
  onWymiana,
  onPropozycja,
  kursDo,
}: {
  view: SuperfarmerView;
  nicki: Record<string, string>;
  mojeId: string;
  wybrany: { kurs: string; odwrotnie: boolean } | null;
  razy: number;
  onWybierz: (wybor: { kurs: string; odwrotnie: boolean } | null) => void;
  onRazy: (razy: number) => void;
  onWymiana: (kurs: string, razy: number, odwrotnie: boolean) => void;
  onPropozycja: (komu: string, kurs: string, razy: number, odwrotnie: boolean) => void;
  kursDo: (id: string) => { id: string; oddaje: Strona; bierze: Strona } | null;
}) {
  if (view.wymianaZuzyta) {
    return <Komunikat>Wymiana w tej turze już wykorzystana — zostaje rzut.</Komunikat>;
  }

  const dozwolony = (kurs: string, odwrotnie: boolean) =>
    view.dozwoloneKursy.find((d) => d.kurs === kurs && d.odwrotnie === odwrotnie) ?? null;
  const stacMnie = (kurs: string, odwrotnie: boolean) =>
    view.mojeKursy.find((d) => d.kurs === kurs && d.odwrotnie === odwrotnie) ?? null;

  if (wybrany) {
    const kurs = kursDo(wybrany.kurs);
    if (!kurs) return null;
    const zeStadem = dozwolony(wybrany.kurs, wybrany.odwrotnie);
    const moje = stacMnie(wybrany.kurs, wybrany.odwrotnie);
    // Suwak sięga tak daleko, jak pozwala którakolwiek z dróg — o legalności
    // konkretnej i tak rozstrzyga serwer.
    const maks = Math.max(zeStadem?.maks ?? 0, moje?.maks ?? 0) || 1;

    return (
      <Karta>
        <div className="flex items-center justify-between mb-3">
          <ParaKursu kurs={kurs} odwrotnie={wybrany.odwrotnie} nazwy={view.nazwy} razy={razy} />
          <Przycisk wariant="cichy" onClick={() => onWybierz(null)}>
            Zmień
          </Przycisk>
        </div>

        {maks > 1 ? (
          <div className="flex items-center gap-3 mb-3">
            <Przycisk onClick={() => onRazy(Math.max(1, razy - 1))} disabled={razy <= 1}>
              −
            </Przycisk>
            <span className="tabular text-lg w-10 text-center">×{razy}</span>
            <Przycisk onClick={() => onRazy(Math.min(maks, razy + 1))} disabled={razy >= maks}>
              +
            </Przycisk>
          </div>
        ) : null}

        <div className="space-y-2">
          <Przycisk
            wariant="glowny"
            pelna
            disabled={!zeStadem || razy > (zeStadem?.maks ?? 0)}
            onClick={() => {
              onWymiana(wybrany.kurs, razy, wybrany.odwrotnie);
              onWybierz(null);
            }}
          >
            Ze stadem głównym
          </Przycisk>

          {view.wymianaMiedzyGraczami
            ? view.players
                .filter((id) => id !== mojeId)
                .map((id) => (
                  <Przycisk
                    key={id}
                    pelna
                    disabled={razy > (moje?.maks ?? 0)}
                    onClick={() => {
                      onPropozycja(id, wybrany.kurs, razy, wybrany.odwrotnie);
                      onWybierz(null);
                    }}
                  >
                    Zaproponuj: {nicki[id] ?? id}
                  </Przycisk>
                ))
            : null}
        </div>
      </Karta>
    );
  }

  return (
    <Karta>
      <div className="text-sm text-tekst-slaby mb-2">Wymiana — jedna na turę, przed rzutem</div>
      <div className="grid grid-cols-2 gap-2">
        {view.kursy.flatMap((kurs) =>
          [false, true].map((odwrotnie) => {
            // Wyróżniony, jeśli da się nim ruszyć którąkolwiek drogą — ze stadem
            // albo z graczem. Reszta zostaje klikalna, tylko przygaszona.
            const mozliwy =
              dozwolony(kurs.id, odwrotnie) !== null || stacMnie(kurs.id, odwrotnie) !== null;
            return (
              <button
                key={`${kurs.id}:${String(odwrotnie)}`}
                onClick={() => onWybierz({ kurs: kurs.id, odwrotnie })}
                className={[
                  "flex items-center justify-center gap-1 rounded-2xl border p-2 min-h-14",
                  mozliwy ? "border-akcent" : "border-obrys opacity-50",
                ].join(" ")}
              >
                <ParaKursu kurs={kurs} odwrotnie={odwrotnie} nazwy={view.nazwy} razy={1} />
              </button>
            );
          }),
        )}
      </div>
    </Karta>
  );
}

/** „6 królików → 1 owca" w postaci obrazkowej. */
function ParaKursu({
  kurs,
  odwrotnie,
  nazwy,
  razy,
}: {
  kurs: { oddaje: Strona; bierze: Strona };
  odwrotnie: boolean;
  nazwy: Record<string, string>;
  razy: number;
}) {
  const lewa = odwrotnie ? kurs.bierze : kurs.oddaje;
  const prawa = odwrotnie ? kurs.oddaje : kurs.bierze;
  return (
    <span className="flex items-center gap-1">
      <span className="tabular">{lewa.ile * razy}</span>
      <Kartonik zwierze={lewa.zwierze} rozmiar="maly" />
      <span className="text-tekst-slaby">→</span>
      <span className="tabular">{prawa.ile * razy}</span>
      <Kartonik zwierze={prawa.zwierze} rozmiar="maly" />
      <span className="sr-only">
        {nazwy[lewa.zwierze]} za {nazwy[prawa.zwierze]}
      </span>
    </span>
  );
}

function PropozycjaKarta({
  view,
  nicki,
  mojeId,
  onOdpowiedz,
}: {
  view: SuperfarmerView;
  nicki: Record<string, string>;
  mojeId: string;
  onOdpowiedz: (akceptuj: boolean) => void;
}) {
  const propozycja = view.propozycja;
  if (!propozycja) return null;
  const kurs = view.kursy.find((k) => k.id === propozycja.kurs);
  if (!kurs) return null;

  const dlaMnie = propozycja.do === mojeId;
  const lewa = propozycja.odwrotnie ? kurs.bierze : kurs.oddaje;
  const prawa = propozycja.odwrotnie ? kurs.oddaje : kurs.bierze;

  return (
    <Karta>
      <div className="text-sm text-tekst-slaby mb-2">
        {dlaMnie
          ? `${nicki[propozycja.od] ?? propozycja.od} proponuje wymianę`
          : `Czekasz na odpowiedź: ${nicki[propozycja.do] ?? propozycja.do}`}
      </div>

      <div className="flex items-center justify-center gap-3 py-2">
        <span className="flex items-center gap-1">
          <span className="tabular text-lg">{lewa.ile * propozycja.razy}</span>
          <Kartonik zwierze={lewa.zwierze} />
        </span>
        <span className="text-tekst-slaby">za</span>
        <span className="flex items-center gap-1">
          <span className="tabular text-lg">{prawa.ile * propozycja.razy}</span>
          <Kartonik zwierze={prawa.zwierze} />
        </span>
      </div>

      {dlaMnie ? (
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Przycisk wariant="glowny" onClick={() => onOdpowiedz(true)}>
            Przyjmuję
          </Przycisk>
          <Przycisk onClick={() => onOdpowiedz(false)}>Odrzucam</Przycisk>
        </div>
      ) : null}
    </Karta>
  );
}

function Wyniki({ view, nicki }: { view: SuperfarmerView; nicki: Record<string, string> }) {
  /*
   * Kolejność z listy Superfarmerów, nie z majątku: partię wygrywa ten, kto
   * pierwszy zebrał komplet, choćby sąsiad miał więcej królików.
   */
  const kolejnosc = [
    ...view.superfarmerzy,
    ...view.players.filter((id) => !view.superfarmerzy.includes(id)),
  ];

  return (
    <>
      <Karta>
        <div className="text-sm text-tekst-slaby mb-3">Wynik końcowy</div>
        <div className="space-y-2">
          {kolejnosc.map((id, miejsce) => (
            <div key={id} className="flex items-center gap-3">
              <span className="w-6 text-tekst-slaby tabular">{miejsce + 1}.</span>
              <span className="flex-1 truncate">
                {nicki[id] ?? id}
                {view.superfarmerzy[0] === id ? " 🏆" : ""}
              </span>
            </div>
          ))}
        </div>
      </Karta>

      {kolejnosc.map((id) => (
        <Karta key={id}>
          <div className="text-sm text-tekst-slaby mb-2">{nicki[id] ?? id}</div>
          <Stado stado={view.stada[id] ?? {}} komplet={view.komplet} nazwy={view.nazwy} maly />
        </Karta>
      ))}
    </>
  );
}
